import React, { useState, useEffect, useCallback } from "react";
import { supabase } from "./supabaseClient";
import { getLevelForXP } from "./Gamification";

function generateCode() {
  return Math.random().toString(36).substring(2, 8).toUpperCase();
}

function GymRooms({ user, role }) {
  const [myGym, setMyGym] = useState(null);
  const [joinedGyms, setJoinedGyms] = useState([]);
  const [activeGym, setActiveGym] = useState(null);
  const [gymMembers, setGymMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [joinCode, setJoinCode] = useState("");
  const [gymName, setGymName] = useState("");
  const [gymDesc, setGymDesc] = useState("");
  const [creating, setCreating] = useState(false);
  const [joining, setJoining] = useState(false);
  const [message, setMessage] = useState(null);
  const [tab, setTab] = useState("leaderboard");

  const loadGyms = useCallback(async () => {
    setLoading(true);
    if (role === "coach") {
      const { data } = await supabase.from("gyms").select("*").eq("coach_id", user.id).single();
      setMyGym(data);
      if (data) setActiveGym(data);
    }
    const { data: memberData } = await supabase.from("gym_members").select("*, gyms(*)").eq("user_id", user.id);
    setJoinedGyms(memberData?.map(m => m.gyms) || []);
    if (!activeGym && memberData?.length > 0) setActiveGym(memberData[0].gyms);
    setLoading(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.id, role]);

  useEffect(() => {
    loadGyms();
  }, [loadGyms]);

  const loadGymMembers = useCallback(async (gymId) => {
    const { data: members } = await supabase
      .from("gym_members")
      .select("*")
      .eq("gym_id", gymId);

    if (!members || members.length === 0) { setGymMembers([]); return; }

    const userIds = members.map(m => m.user_id);

    const { data: profilesData } = await supabase
      .from("profiles")
      .select("id, full_name, avatar_url, boxing_style")
      .in("id", userIds);

    const { data: statsData } = await supabase
      .from("fighter_stats")
      .select("user_id, xp, level, streak, total_uploads")
      .in("user_id", userIds);

    console.log('members:', members);
    console.log('profiles:', profilesData);
    console.log('stats:', statsData);
    const enriched = members.map(m => ({
      ...m,
      profiles: profilesData?.find(p => p.id === m.user_id) || null,
      fighter_stats: statsData?.find(s => s.user_id === m.user_id) || null
    }));

    setGymMembers(enriched);
  }, []);

  useEffect(() => {
    if (activeGym) loadGymMembers(activeGym.id);
  }, [activeGym, loadGymMembers]);

  const handleCreateGym = async () => {
    if (!gymName) return;
    setCreating(true);
    const code = generateCode();
    const { data, error } = await supabase.from("gyms").insert({
      name: gymName,
      code,
      coach_id: user.id,
      description: gymDesc
    }).select().single();
    if (!error && data) {
      await supabase.from("gym_members").insert({ gym_id: data.id, user_id: user.id });
      setMyGym(data);
      setActiveGym(data);
      setGymName("");
      setGymDesc("");
      setMessage({ type: "success", text: "Gym created! Share code: " + code });
    }
    setCreating(false);
  };

  const handleJoinGym = async () => {
    if (!joinCode) return;
    setJoining(true);
    setMessage(null);
    const { data: gym } = await supabase.from("gyms").select("*").eq("code", joinCode.toUpperCase()).single();
    if (!gym) {
      setMessage({ type: "error", text: "Gym not found. Check the code and try again." });
      setJoining(false);
      return;
    }
    const { error } = await supabase.from("gym_members").insert({ gym_id: gym.id, user_id: user.id });
    if (error) {
      setMessage({ type: "error", text: "Already a member of this gym." });
    } else {
      setJoinedGyms(prev => [...prev, gym]);
      setActiveGym(gym);
      setJoinCode("");
      setMessage({ type: "success", text: "Joined " + gym.name + "!" });
    }
    setJoining(false);
  };

  const sortedMembers = [...gymMembers].sort((a, b) => {
    if (tab === "leaderboard") return (b.fighter_stats?.xp || 0) - (a.fighter_stats?.xp || 0);
    if (tab === "streak") return (b.fighter_stats?.streak || 0) - (a.fighter_stats?.streak || 0);
    return (b.fighter_stats?.total_uploads || 0) - (a.fighter_stats?.total_uploads || 0);
  });

  const allGyms = role === "coach" && myGym ? [myGym, ...joinedGyms.filter(g => g.id !== myGym.id)] : joinedGyms;

  return (
    <div style={{fontFamily:"Inter, sans-serif"}}>
      <h2 style={{fontFamily:"Bebas Neue, sans-serif",fontSize:"2.5rem",letterSpacing:"2px",background:"linear-gradient(135deg, #e94560, #ff6b35)",WebkitBackgroundClip:"text",WebkitTextFillColor:"transparent",marginBottom:"8px",marginTop:0}}>GYM ROOMS</h2>
      <p style={{color:"#555",marginBottom:"25px",fontSize:"0.9rem"}}>Train together. Compete together. Grow together.</p>

      {role === "coach" && !myGym && (
        <div style={{background:"rgba(255,255,255,0.02)",border:"1px solid rgba(255,255,255,0.06)",borderRadius:"16px",padding:"25px",marginBottom:"20px"}}>
          <h3 style={{color:"white",margin:"0 0 15px 0",fontSize:"1.1rem"}}>Create Your Gym</h3>
          <input type="text" placeholder="Gym name (e.g. Champions Boxing Club)" value={gymName} onChange={e => setGymName(e.target.value)} style={{width:"100%",padding:"14px",backgroundColor:"rgba(255,255,255,0.04)",color:"white",border:"1px solid rgba(255,255,255,0.08)",borderRadius:"12px",fontSize:"0.95rem",fontFamily:"Inter, sans-serif",boxSizing:"border-box",outline:"none",marginBottom:"12px"}} />
          <input type="text" placeholder="Description (optional)" value={gymDesc} onChange={e => setGymDesc(e.target.value)} style={{width:"100%",padding:"14px",backgroundColor:"rgba(255,255,255,0.04)",color:"white",border:"1px solid rgba(255,255,255,0.08)",borderRadius:"12px",fontSize:"0.95rem",fontFamily:"Inter, sans-serif",boxSizing:"border-box",outline:"none",marginBottom:"12px"}} />
          <button onClick={handleCreateGym} disabled={!gymName || creating} style={{width:"100%",padding:"14px",background: !gymName || creating ? "rgba(255,255,255,0.05)" : "linear-gradient(135deg, #e94560, #ff6b35)",color: !gymName || creating ? "#444" : "white",border:"none",borderRadius:"12px",cursor: !gymName || creating ? "not-allowed" : "pointer",fontFamily:"Inter, sans-serif",fontWeight:"700",fontSize:"0.95rem",letterSpacing:"1px",textTransform:"uppercase"}}>
            {creating ? "Creating..." : "Create Gym Room"}
          </button>
        </div>
      )}

      {role === "coach" && myGym && (
        <div style={{background:"linear-gradient(135deg, rgba(233,69,96,0.1), rgba(255,107,53,0.05))",border:"1px solid rgba(233,69,96,0.2)",borderRadius:"16px",padding:"20px",marginBottom:"20px"}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
            <div>
              <p style={{color:"#e94560",margin:"0 0 4px 0",fontSize:"0.75rem",textTransform:"uppercase",letterSpacing:"1px"}}>Your Gym</p>
              <h3 style={{color:"white",margin:0,fontSize:"1.2rem",fontWeight:"700"}}>{myGym.name}</h3>
            </div>
            <div style={{textAlign:"right"}}>
              <p style={{color:"#555",margin:"0 0 4px 0",fontSize:"0.75rem"}}>Invite Code</p>
              <div style={{background:"rgba(0,0,0,0.4)",border:"1px solid rgba(233,69,96,0.3)",borderRadius:"8px",padding:"8px 16px"}}>
                <span style={{fontFamily:"Bebas Neue, sans-serif",fontSize:"1.5rem",letterSpacing:"3px",color:"#e94560"}}>{myGym.code}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      <div style={{background:"rgba(255,255,255,0.02)",border:"1px solid rgba(255,255,255,0.06)",borderRadius:"16px",padding:"25px",marginBottom:"20px"}}>
        <h3 style={{color:"white",margin:"0 0 15px 0",fontSize:"1rem"}}>Join a Gym</h3>
        <div style={{display:"flex",gap:"10px"}}>
          <input type="text" placeholder="Enter gym code" value={joinCode} onChange={e => setJoinCode(e.target.value.toUpperCase())} style={{flex:1,padding:"14px",backgroundColor:"rgba(255,255,255,0.04)",color:"white",border:"1px solid rgba(255,255,255,0.08)",borderRadius:"12px",fontSize:"0.95rem",fontFamily:"Inter, sans-serif",outline:"none",letterSpacing:"2px"}} />
          <button onClick={handleJoinGym} disabled={!joinCode || joining} style={{padding:"14px 20px",background: !joinCode || joining ? "rgba(255,255,255,0.05)" : "linear-gradient(135deg, #e94560, #ff6b35)",color: !joinCode || joining ? "#444" : "white",border:"none",borderRadius:"12px",cursor: !joinCode || joining ? "not-allowed" : "pointer",fontFamily:"Inter, sans-serif",fontWeight:"700",fontSize:"0.9rem",whiteSpace:"nowrap"}}>
            {joining ? "Joining..." : "Join Gym"}
          </button>
        </div>
        {message && <p style={{color: message.type === "error" ? "#e94560" : "#4caf50",margin:"10px 0 0 0",fontSize:"0.9rem"}}>{message.text}</p>}
      </div>

      {allGyms.length > 0 && (
        <div style={{display:"flex",gap:"8px",marginBottom:"20px",flexWrap:"wrap"}}>
          {allGyms.map(gym => (
            <button key={gym.id} onClick={() => setActiveGym(gym)} style={{padding:"10px 18px",borderRadius:"10px",cursor:"pointer",fontFamily:"Inter, sans-serif",fontSize:"0.85rem",fontWeight:"600",border: activeGym?.id === gym.id ? "none" : "1px solid rgba(255,255,255,0.08)",background: activeGym?.id === gym.id ? "linear-gradient(135deg, #e94560, #ff6b35)" : "rgba(255,255,255,0.03)",color: activeGym?.id === gym.id ? "white" : "#666"}}>
              {gym.name}
            </button>
          ))}
        </div>
      )}

      {activeGym && (
        <div>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:"20px"}}>
            <h3 style={{color:"white",margin:0,fontSize:"1.1rem",fontWeight:"700"}}>{activeGym.name}</h3>
            <span style={{color:"#444",fontSize:"0.8rem"}}>{gymMembers.length} members</span>
          </div>

          <div style={{display:"flex",gap:"8px",marginBottom:"20px"}}>
            {[{id:"leaderboard",label:"⭐ XP"},{id:"streak",label:"🔥 Streak"},{id:"sessions",label:"📹 Sessions"}].map(t => (
              <button key={t.id} onClick={() => setTab(t.id)} style={{padding:"8px 16px",borderRadius:"10px",cursor:"pointer",fontFamily:"Inter, sans-serif",fontSize:"0.85rem",fontWeight:"600",border: tab===t.id ? "none" : "1px solid rgba(255,255,255,0.08)",background: tab===t.id ? "linear-gradient(135deg, #e94560, #ff6b35)" : "rgba(255,255,255,0.03)",color: tab===t.id ? "white" : "#666"}}>
                {t.label}
              </button>
            ))}
          </div>

          {sortedMembers.length === 0 ? (
            <div style={{background:"rgba(255,255,255,0.02)",padding:"40px",borderRadius:"16px",textAlign:"center",border:"1px solid rgba(255,255,255,0.05)"}}>
              <p style={{color:"#555"}}>No members yet. Share the gym code to invite fighters!</p>
            </div>
          ) : (
            sortedMembers.map((member, index) => {
              const isMe = member.user_id === user.id;
              const name = member.profiles?.full_name || "Fighter";
              const avatar = member.profiles?.avatar_url;
              const stats = member.fighter_stats;
              const level = getLevelForXP(stats?.xp || 0);
              const value = tab === "leaderboard" ? (stats?.xp || 0) + " XP" : tab === "streak" ? (stats?.streak || 0) + " days" : (stats?.total_uploads || 0) + " sessions";
              const medal = index === 0 ? "🥇" : index === 1 ? "🥈" : index === 2 ? "🥉" : null;

              return (
                <div key={member.id} style={{display:"flex",alignItems:"center",gap:"15px",padding:"16px 20px",borderRadius:"14px",marginBottom:"10px",background: isMe ? "linear-gradient(135deg, rgba(233,69,96,0.12), rgba(255,107,53,0.06))" : "rgba(255,255,255,0.02)",border: isMe ? "1px solid rgba(233,69,96,0.3)" : "1px solid rgba(255,255,255,0.05)"}}>
                  <div style={{width:"35px",textAlign:"center",flexShrink:0}}>
                    {medal ? <span style={{fontSize:"1.3rem"}}>{medal}</span> : <span style={{color:"#444",fontSize:"0.9rem",fontWeight:"600"}}>#{index+1}</span>}
                  </div>
                  <div style={{width:"40px",height:"40px",borderRadius:"50%",background: avatar ? "transparent" : "linear-gradient(135deg, #e94560, #ff6b35)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:"1rem",overflow:"hidden",flexShrink:0,border:"2px solid rgba(255,255,255,0.08)"}}>
                    {avatar ? <img src={avatar} alt="" style={{width:"100%",height:"100%",objectFit:"cover"}} /> : "🥋"}
                  </div>
                  <div style={{flex:1}}>
                    <p style={{color: isMe ? "#e94560" : "white",margin:0,fontWeight:"600",fontSize:"0.9rem"}}>{name}{isMe ? " (You)" : ""}</p>
                    <p style={{color:"#555",margin:"2px 0 0 0",fontSize:"0.75rem"}}>{level.name} • Level {level.level}</p>
                  </div>
                  <div style={{textAlign:"right"}}>
                    <p style={{color: index < 3 ? "#e94560" : "white",margin:0,fontWeight:"700",fontSize:"0.95rem"}}>{value}</p>
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}

      {allGyms.length === 0 && !loading && role !== "coach" && (
        <div style={{background:"rgba(255,255,255,0.02)",padding:"50px",borderRadius:"16px",textAlign:"center",border:"1px solid rgba(255,255,255,0.05)"}}>
          <div style={{fontSize:"3rem",marginBottom:"15px"}}>🏋️</div>
          <p style={{color:"#555",fontSize:"1rem"}}>You haven't joined a gym yet. Ask your coach for their gym code!</p>
        </div>
      )}
    </div>
  );
}

export default GymRooms;
